'use client';

import { Button, DialogFooter, ErrorState, Spinner, Stack, Text } from '@pie/amber';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeError } from '@stripe/stripe-js';
import { reporter } from '@upms/observability';
import { useState } from 'react';
import { AffectedPolicy } from '@/components/PaymentDialog/AffectedPolicy';
import { PaymentAcknowledgment } from '@/components/PaymentDialog/PaymentAcknowledgment';
import { type PaymentMethodType } from '@/lib/api/billing';
import {
  classifyStripeError,
  type StripeErrorClassification,
} from '@/lib/stripe/classifyStripeError';
import { StripeMethodSelector } from './StripeMethodSelector';
import { fullName, useIdentityContext } from '../../IdentityContext';
import { ErrorBody } from '@/components/PaymentDialog/ErrorBody';

/**
 * Static <PaymentElement> options. Module constant so its identity is stable —
 * Stripe diffs `options` on every render, and a fresh object each time is churn.
 */
const PAYMENT_ELEMENT_OPTIONS = {
  // Single-method session hides the tab bar; keep tabs in case a session ever
  // carries more than one method.
  layout: { type: 'tabs', defaultCollapsed: false },
  fields: {
    billingDetails: {
      email: 'never',
      name: 'never',
    },
  },
  // Hide Stripe's built-in mandate text — we render our own Acknowledgment below.
  terms: {
    card: 'never',
    usBankAccount: 'never',
  },
} as const;

type PaymentFormProps = {
  /** Payment session (re)fetching on a method switch; gates submit. */
  isPaymentSessionLoading: boolean;
  /** SetupIntent secret for the selected method; used only by `confirmSetup`. */
  clientSecret: string;
  policyId: string;
  policyNumber: string;
  paymentMethodType: PaymentMethodType;
  onPaymentMethodTypeChange: (value: PaymentMethodType) => void;
  onSuccess: () => void;
  hasExistingPaymentMethod: boolean;
};

export const PaymentForm = ({
  isPaymentSessionLoading,
  clientSecret,
  policyId,
  policyNumber,
  paymentMethodType,
  onPaymentMethodTypeChange,
  onSuccess,
  hasExistingPaymentMethod,
}: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useIdentityContext();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<StripeErrorClassification | null>(null);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const reportAndSetError = (stripeError: StripeError) => {
    const classification = classifyStripeError(stripeError);
    if (classification.loggable) {
      reporter.error(
        `Stripe Error: ${stripeError.message}`,
        {
          feature: 'stripe',
          operation: 'payment-method-update',
          type: stripeError.type,
          code: stripeError.code ?? 'none',
          policyId,
          paymentMethodType,
        },
        stripeError
      );
    }
    setError(classification);
  };

  const handleMethodTypeChange = (value: PaymentMethodType) => {
    // Drop any banner from the previous method — it no longer describes the form.
    setError(null);
    onPaymentMethodTypeChange(value);
  };

  const submit = async () => {
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setSubmitting(false);
      reportAndSetError(submitError);
      return;
    }

    const returnUrl = `${window.location.origin}${window.location.pathname}`;

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        return_url: returnUrl,
        // email/name are hidden in the iframe (fields.billingDetails), so supply
        // them here from the authenticated user record.
        payment_method_data: {
          billing_details: { name: fullName(user), email: user.email },
        },
      },
      redirect: 'if_required',
    });
    setSubmitting(false);

    if (confirmError) {
      // A declined SetupIntent returns to `requires_payment_method` with a
      // reusable clientSecret, so the form stays and the user resubmits against
      // the same intent — no re-mint (unlike Zuora's single-use HPM signature).
      reportAndSetError(confirmError);
      return;
    }
    if (setupIntent?.status === 'succeeded' || setupIntent?.status === 'processing') {
      reporter.action('payment_method_updated', {
        feature: 'stripe',
        operation: 'payment-method-update',
        policyId,
        paymentMethodType,
      });
      onSuccess();
    }
  };

  if (loadFailed) {
    return <ErrorBody />;
  }

  return (
    <>
      {/*
       * Plain scroll container instead of Amber's <DialogBody>: DialogBody's
       * <Scrollable> measures then restyles the element it measures from inside a
       * state updater. The Stripe iframe resizes as it loads, so near the scroll
       * threshold that feedback runs away into "Maximum update depth exceeded"
       * (PDESIGN-415). A plain overflow container scrolls without the feedback.
       */}
      <div className="flex-1 overflow-y-auto px-layout-sm md:px-0 pb-layout-xs">
        <Stack gap="layout-md">
          {error && <ErrorState heading={error.heading} helpText={error.body} size="md" />}
          <AffectedPolicy policyNumber={policyNumber} />
          <StripeMethodSelector value={paymentMethodType} onValueChange={handleMethodTypeChange} />
          {/*
           * A single, stable Payment Element — switching updates `<Elements>`
           * paymentMethodTypes in place rather than remounting. While the session
           * refetches, cover it with a spinner via visibility (NOT unmount, so
           * height stays reserved) to block interaction until clientSecret catches up.
           */}
          <div className="relative w-full">
            <div className={ready && !isPaymentSessionLoading ? 'w-full' : 'invisible'}>
              <PaymentElement
                className="w-full"
                options={PAYMENT_ELEMENT_OPTIONS}
                onReady={() => setReady(true)}
                onLoadError={() => setLoadFailed(true)}
              />
            </div>
            {(!ready || isPaymentSessionLoading) && (
              <Stack
                align="center"
                className="absolute inset-0"
                direction="column"
                gap="2xs"
                justify="center"
              >
                <Spinner className="text-text-subtle-default" size="lg" title="Payment form" />
                <Text emphasis="subtle" variant="body-xs">
                  Loading
                </Text>
              </Stack>
            )}
          </div>
          <PaymentAcknowledgment />
        </Stack>
      </div>
      <DialogFooter>
        <Stack direction="row" gap="sm" justify="end">
          <Button
            tone="accent"
            emphasis="heavy"
            loading={submitting}
            onClick={submit}
            disabled={!ready || isPaymentSessionLoading || !stripe || submitting}
            size={{ base: 'lg', sm: 'md' }}
            fluid={{ base: true, sm: false }}
          >
            {hasExistingPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
};
